import { useState, useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

export default function W2vs1099Comparison() {
  useEffect(() => {
    document.title = "W-2 vs 1099 Tax Comparison Calculator | Employee vs Contractor - ItsJustTax";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Compare W-2 employee vs 1099 contractor taxes. Calculate the difference in take-home pay, self-employment tax, and total tax liability. Understand which employment status is better for your financial situation.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'W-2 vs 1099, employee vs contractor, self-employment tax, 1099 contractor taxes, W-2 employee taxes');
    }
  }, []);

  const [income, setIncome] = useState('');
  const [benefits, setBenefits] = useState('');
  const [expenses, setExpenses] = useState('');
  const [result, setResult] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);

  const calculate = () => {
    const annualIncome = parseFloat(income);
    const benefitsValue = parseFloat(benefits) || 0;
    const businessExpenses = parseFloat(expenses) || 0;

    if (isNaN(annualIncome) || annualIncome <= 0) {
      alert('Please enter a valid income amount');
      return;
    }

    // W-2 Employee Calculations
    const w2SocialSecurity = annualIncome * 0.062;
    const w2Medicare = annualIncome * 0.0145;
    const w2TotalPayrollTax = w2SocialSecurity + w2Medicare;
    const w2TaxableIncome = annualIncome - 14600; // Standard deduction
    const w2IncomeTax = calculateIncomeTax(Math.max(0, w2TaxableIncome));
    const w2TotalTax = w2TotalPayrollTax + w2IncomeTax;
    const w2NetIncome = annualIncome - w2TotalTax + benefitsValue;

    // 1099 Contractor Calculations
    const netBusinessIncome = annualIncome - businessExpenses;
    const contractor1099SelfEmploymentTax = netBusinessIncome * 0.9235 * 0.153;
    const deductibleSETax = contractor1099SelfEmploymentTax * 0.5;
    const contractor1099TaxableIncome = netBusinessIncome - deductibleSETax - 14600;
    const contractor1099IncomeTax = calculateIncomeTax(Math.max(0, contractor1099TaxableIncome));
    const contractor1099TotalTax = contractor1099SelfEmploymentTax + contractor1099IncomeTax;
    const contractor1099NetIncome = annualIncome - businessExpenses - contractor1099TotalTax;

    setResult({
      w2: {
        grossIncome: annualIncome,
        payrollTax: w2TotalPayrollTax,
        incomeTax: w2IncomeTax,
        totalTax: w2TotalTax,
        benefits: benefitsValue,
        netIncome: w2NetIncome
      },
      contractor1099: {
        grossIncome: annualIncome,
        expenses: businessExpenses,
        selfEmploymentTax: contractor1099SelfEmploymentTax,
        incomeTax: contractor1099IncomeTax,
        totalTax: contractor1099TotalTax,
        netIncome: contractor1099NetIncome
      },
      difference: contractor1099NetIncome - w2NetIncome
    });
  };

  const calculateIncomeTax = (taxableIncome: number) => {
    if (taxableIncome <= 11600) return taxableIncome * 0.10;
    if (taxableIncome <= 47150) return 1160 + (taxableIncome - 11600) * 0.12;
    if (taxableIncome <= 100525) return 5426 + (taxableIncome - 47150) * 0.22;
    if (taxableIncome <= 191950) return 17168.50 + (taxableIncome - 100525) * 0.24;
    if (taxableIncome <= 243725) return 39110.50 + (taxableIncome - 191950) * 0.32;
    if (taxableIncome <= 609350) return 55678.50 + (taxableIncome - 243725) * 0.35;
    return 183647.50 + (taxableIncome - 609350) * 0.37;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8">
            <a href="/" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">W-2 vs 1099 Comparison</h1>
            <p className="text-lg text-gray-600">Compare the financial impact of being an employee versus an independent contractor</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Enter Your Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Income</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={income}
                      onChange={(e) => setIncome(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="80000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Employer Benefits Value (W-2 Only)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={benefits}
                      onChange={(e) => setBenefits(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="12000"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Health insurance, 401k match, etc.</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Business Expenses (1099 Only)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={expenses}
                      onChange={(e) => setExpenses(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="8000"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Equipment, software, home office, etc.</p>
                </div>

                <button
                  onClick={calculate}
                  className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-teal-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Compare Options
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 shadow-lg text-white">
              <h2 className="text-2xl font-bold mb-6">Key Differences</h2>
              
              <div className="space-y-6">
                <div className="bg-white/10 rounded-xl p-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <i className="ri-briefcase-line"></i>
                    W-2 Employee
                  </h3>
                  <ul className="space-y-2 text-sm text-blue-100">
                    <li>• Employer pays half of payroll taxes</li>
                    <li>• Benefits often included</li>
                    <li>• Taxes withheld automatically</li>
                    <li>• Limited business deductions</li>
                    <li>• More job security</li>
                  </ul>
                </div>

                <div className="bg-white/10 rounded-xl p-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <i className="ri-user-star-line"></i>
                    1099 Contractor
                  </h3>
                  <ul className="space-y-2 text-sm text-blue-100">
                    <li>• Pay full self-employment tax (15.3%)</li>
                    <li>• Must provide own benefits</li>
                    <li>• Quarterly estimated tax payments</li>
                    <li>• Can deduct business expenses</li>
                    <li>• More flexibility and control</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {result && (
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <i className="ri-briefcase-line text-2xl text-blue-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">W-2 Employee</h3>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Gross Income</span>
                    <span className="font-semibold">${result.w2.grossIncome.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Payroll Tax (7.65%)</span>
                    <span className="font-semibold text-red-600">-${result.w2.payrollTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Income Tax</span>
                    <span className="font-semibold text-red-600">-${result.w2.incomeTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Benefits Value</span>
                    <span className="font-semibold text-green-600">+${result.w2.benefits.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 bg-blue-50 rounded-xl p-4">
                    <span className="font-semibold text-gray-900">Net Income</span>
                    <span className="text-2xl font-bold text-blue-600">${result.w2.netIncome.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                    <i className="ri-user-star-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">1099 Contractor</h3>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Gross Income</span>
                    <span className="font-semibold">${result.contractor1099.grossIncome.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Business Expenses</span>
                    <span className="font-semibold text-red-600">-${result.contractor1099.expenses.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Self-Employment Tax</span>
                    <span className="font-semibold text-red-600">-${result.contractor1099.selfEmploymentTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Income Tax</span>
                    <span className="font-semibold text-red-600">-${result.contractor1099.incomeTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 bg-teal-50 rounded-xl p-4">
                    <span className="font-semibold text-gray-900">Net Income</span>
                    <span className="text-2xl font-bold text-teal-600">${result.contractor1099.netIncome.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 shadow-lg text-white">
                <h3 className="text-2xl font-bold mb-4">Bottom Line</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-300 mb-2">
                      {result.difference > 0 
                        ? 'As a 1099 contractor, you would earn more by:' 
                        : 'As a W-2 employee, you would earn more by:'}
                    </p>
                    <p className="text-4xl font-bold">
                      ${Math.abs(result.difference).toLocaleString(undefined, {maximumFractionDigits: 2})}
                    </p>
                  </div>
                  <div className="text-6xl">
                    {result.difference > 0 ? '📈' : '💼'}
                  </div>
                </div>
                <p className="text-gray-300 text-sm mt-6 leading-relaxed">
                  Remember: This comparison doesn't include factors like job security, benefits quality, 
                  work flexibility, and potential for additional contractor income. Consider your full situation 
                  when making employment decisions.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}